import { React } from 'Imports';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { ConfirmDialog } from './ConfirmDialog';
import { VideoEventService } from '$State/VideoEventFreezerService';
import * as scssStyles from '../../css/settings.scss';
import { VideoEventFilterServiceMultiselect } from '$State/VideoEventFilterFreezerServiceMultiselect';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { useEffect } from 'react';

interface IDeleteConfirmationProps {
    eventIds?: number[];
    showDeletePopup: boolean;
    toggleDeletePopup: (selectedId?: number) => void;
    changeToDisabled: (disabled: boolean) => void;
    refreshVideoEvent?: () => void;
    isBulk?: boolean;
    onDelete?: () => void;
    onUndoDelete?: () => void;
    skipUpdateListOnSuccess?: boolean;
}

export const DeleteConfirmation = (props: IDeleteConfirmationProps) => {
    const { showSnackbarMessage, handleCloseSnackbar } = useAppSnackbar();

    const {
        eventIds = [],
        showDeletePopup,
        isBulk,
        changeToDisabled,
        toggleDeletePopup,
        refreshVideoEvent,
        onDelete,
        onUndoDelete,
        skipUpdateListOnSuccess,
    } = props;

    const isEncompass = scssStyles.styleEnvironment === 'encompass';
    const isAlert = isEncompass;
    const acceptButton = isEncompass ? 'Yes, Delete' : 'Delete';
    const cancelButton = isEncompass ? 'No, Go back' : 'Cancel';
    const showIcon = isEncompass;
    const numEvents = eventIds.length;
    const contentMessage = `Are you sure you want to delete ${!isBulk ? 'this' : isEncompass ? 'these' : numEvents} video event${
        numEvents > 1 ? 's' : ''
    }?`;

    useEffect(() => {
        return () => {
            handleCloseSnackbar();
        };
    }, []);

    const handleToggleDeletePopup = (e: any) => {
        e.stopPropagation();

        if (toggleDeletePopup) toggleDeletePopup();
    };

    const handleSubmit = async (e: any, undo: boolean): Promise<void> => {
        e.stopPropagation();
        handleCloseSnackbar(`event-${!undo ? 'restored' : 'deleted'}`);

        const body = {
            videoEventIdList: eventIds,
        };

        const deleteResult = undo ? await VideoEventService.restoreVideoEvent(body) : await VideoEventService.deleteVideoEvent(body);

        if (deleteResult) {
            showSnackbarMessage({
                message: `Event${eventIds.length > 1 ? 's' : ''} ${undo ? 'restored' : 'deleted'} successfully.`,
                autoHideDuration: undo ? 5000 : null,
                key: `event-${undo ? 'restored' : 'deleted'}`,
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onSuccessLinkActionClick: () => handleSubmit(e, true),
                successLinkActionText: undo ? undefined : 'Undo Delete',
                closeOnOutsideClick: !undo,
            });
            changeToDisabled(!undo);

            if (!skipUpdateListOnSuccess) {
                const videoEventFilter = VideoEventFilterServiceMultiselect.getVideoEventFilter();
                await VideoEventService.getListOfEventIds(videoEventFilter);
            }
            if (onDelete && !undo) onDelete();
            if (onUndoDelete && undo) onUndoDelete();

            setTimeout(() => {
                if (refreshVideoEvent) {
                    refreshVideoEvent();
                    changeToDisabled(false);
                }
            }, 2000);
        } else {
            showSnackbarMessage({
                message: `Unable to ${undo ? 'restore' : 'delete'} event${eventIds.length > 1 ? 's' : ''}.`,
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
            changeToDisabled(undo);
        }

        if (toggleDeletePopup && !undo) toggleDeletePopup();
    };

    const defineTitle = () =>
        isBulk && isEncompass
            ? `Delete ${numEvents} Video Event${numEvents > 1 ? 's' : ''}?`
            : isEncompass
              ? `Delete Video Event`
              : `Confirm Delete`;

    return (
        <>
            <ConfirmDialog
                isOpen={showDeletePopup}
                title={defineTitle()}
                contentMessage={contentMessage}
                cancelButton={cancelButton}
                acceptButton={acceptButton}
                isAlert={isAlert}
                alertType={AlertTypes.Alert}
                showIcon={showIcon}
                handleModalClosed={(e) => handleToggleDeletePopup(e)}
                handleSubmit={(e) => handleSubmit(e, false)}
                isButtonAlert={true}
                contentMessageMarginTop={isEncompass ? '0' : undefined}
            />
        </>
    );
};
